import { TenantIds } from '@config/tenant-id';
import { EnumSelect } from '@summit/core';

export enum PayrollSource {
  None = 0,
  EmployeeWageExtra = 1,
  IncentiveGroup = 20,
  CancelledRun = 21,
  EquipmentMove = 22,
  DOTInspection = 23,

  //Employee Adj Types 1000 through 1100
  Manual = 1000,
  Weekly = 1001,
  Daily = 1002,
  PerLeg = 1003,
  PerLegWithDH = 1004,
  DhPercentage = 1005,
  SoloDhByTrailerNone = 1006,
  SoloDhByTrailerSingle = 1007,
  SoloDhByTrailerDouble = 1008,
  SoloDhByTrailerTriple = 1009,
  TeamDhByTrailerNone = 1010,
  TeamDhByTrailerSingle = 1011,
  TeamDhByTrailerDouble = 1012,
  TeamDhByTrailerTriple = 1013,
  HourlyPayForDepartureDelays = 1014,
  HourlyPayForRerouteDelays = 1015,
  GrossNetFuelPay = 1016,
  MileageSoloCorrection = 1020,
  RouteCorrection = 1021,
  SpotCorrection = 1022,
  PWCorrection = 1023,
  MileageTeamCorrection = 1024,
  Incentive = 1030,
  WageExtra = 1031,
  DaysGuaranteed = 1032,
  DayMilesThreshold = 1033,
  DayStopThreshold = 1034,
  DaySpotThreshold = 1035,
  EmployeeAdjCorrection = 1037,
  MileageSpotCorrection = 1038,
  DayPay6th = 1039,
  EmpAdjSafety = 1040,
  SafetySolo = 1041,
  SafetyTeam = 1042,
  SafetySpot = 1049,
  AvailabilitySolo = 1043,
  AvailabilityTeam = 1044,
  LegWage = 1045,
  IncludeSoloMiles = 1046,
  IncludeTeamMiles = 1047,
  Overtime = 1048,
  Orientation = 1050,
  DayPayCorrection = 1051,
  MileageSoloRuleCorrection = 1052,
  MileageTeamRuleCorrection = 1053,
  Weekend = 1054,
  Trainer = 1055,
  Trainee = 1056,
  AvailabilitySpot = 1057,
  BlockFurtherRules = 1058,
  DhCorrection = 1059,
  DailyOverride = 1060,
  WageExtraDH = 1061,
  DayPay6thDayCorrection = 1062,
  HourlyCorrection = 1063,
  DayPayIncremental = 1064,

  //Spot Pay Sources to replace SpotPayReason
  Combined = 2000,
  Stop = 2001,
  LiveLoad = 2002,
  PerPiece = 2003,
  Override = 2004,
  Gross = 2005,

  //Bonus Types 3000 through 3100
  Unknown = 3000,
  Attendance = 3001,
  Safety = 3002,
  Other = 3003,
  Referral = 3004,
  SignOn = 3005,
  Retention = 3006,
  DropAndHook = 3007,
  Services = 3008,
  Birthday = 3009,
  Efficiency = 3010,

  //Misc Work Types 4000 through 4100
  Training = 4000,
  DetentionPay = 4001,
  WeeklyAdjustment = 4002, //limit to JWI
  AvailableNoWork = 4003,
  Breakdown = 4004,
  LayDown = 4005,
  MaintCoverage = 4006,

  //Reimbursement Types 5000 through 5100
  HealthCareDeduction = 5000,
  HealthCareCredit = 5001,

  perDiemSoloMiles = 6000,
  perDiemTeamMiles = 6001,
  perDiemSoloMilesPrior = 6002,
  perDiemTeamMilesPrior = 6003,

  //Holiday
  HolidayPayDay = 7000,
  HolidayPaySolo = 7001,
  HolidayPayTeam = 7002,

  //Expenses
  Fuel = 8000,
  Tolls = 8001,
  Scale = 8002,
  DEF = 8003,
  Wash = 8004,
  Repair = 8005
}

export const PayrollSourceMapping: Record<PayrollSource, string> = {
  [PayrollSource.None]: '',
  [PayrollSource.EmployeeWageExtra]: 'Employee Wage Extra',
  [PayrollSource.IncentiveGroup]: 'Incentive',
  [PayrollSource.CancelledRun]: 'Cancelled Run',
  [PayrollSource.EquipmentMove]: 'Equipment Move',
  [PayrollSource.DOTInspection]: 'DOT Inspection',
  ////
  [PayrollSource.Manual]: 'Manual',
  [PayrollSource.Weekly]: 'Weekly',
  [PayrollSource.Daily]: 'Daily',
  [PayrollSource.PerLeg]: 'Per Leg Pay',
  [PayrollSource.PerLegWithDH]: 'Per Leg with DH',
  [PayrollSource.DhPercentage]: 'Percentage of DH',
  [PayrollSource.SoloDhByTrailerNone]: 'Per Solo Bobtail',
  [PayrollSource.SoloDhByTrailerSingle]: 'Per Solo Single DH',
  [PayrollSource.SoloDhByTrailerDouble]: 'Per Solo Double DH',
  [PayrollSource.SoloDhByTrailerTriple]: 'Per Solo Triple DH',
  [PayrollSource.TeamDhByTrailerNone]: 'Per Team Bobtail',
  [PayrollSource.TeamDhByTrailerSingle]: 'Per Team Single DH',
  [PayrollSource.TeamDhByTrailerDouble]: 'Per Team Double DH',
  [PayrollSource.TeamDhByTrailerTriple]: 'Per Team Triple DH',
  [PayrollSource.HourlyPayForDepartureDelays]: 'Hourly Pay for Delays',
  [PayrollSource.HourlyPayForRerouteDelays]: 'Hourly Pay for ReRoutes',
  [PayrollSource.GrossNetFuelPay]: '% Gross Pay',
  [PayrollSource.MileageSoloCorrection]: 'Solo Mileage Correction',
  [PayrollSource.RouteCorrection]: 'Route Pay Correction',
  [PayrollSource.SpotCorrection]: 'Spot Pay Correction',
  [PayrollSource.PWCorrection]: 'Prior Week Correction',
  [PayrollSource.MileageTeamCorrection]: 'Team Mileage Correction',
  [PayrollSource.Incentive]: 'Incentive',
  [PayrollSource.WageExtra]: 'Wage',
  [PayrollSource.DaysGuaranteed]: 'Days Guaranteed',
  [PayrollSource.DayMilesThreshold]: 'Day Miles Threshold',
  [PayrollSource.DayStopThreshold]: 'Day Stop Threshold',
  [PayrollSource.DaySpotThreshold]: 'Day Spot Threshold',
  [PayrollSource.Combined]: 'Combined',
  [PayrollSource.Stop]: 'Stop Pay',
  [PayrollSource.LiveLoad]: 'Live Load',
  [PayrollSource.PerPiece]: 'Per Piece',
  [PayrollSource.Override]: 'Override',
  [PayrollSource.Gross]: 'Gross',
  [PayrollSource.EmployeeAdjCorrection]: 'Employee Adjustment Correction',
  [PayrollSource.MileageSpotCorrection]: 'Spot Mileage Correction',
  [PayrollSource.DayPay6th]: '6th Day Pay',
  [PayrollSource.EmpAdjSafety]: 'Safety Pay',
  [PayrollSource.SafetySolo]: 'Safety Solo',
  [PayrollSource.SafetyTeam]: 'Safety Team',
  [PayrollSource.SafetySpot]: 'Safety Spot',
  [PayrollSource.Unknown]: 'Unknown',
  [PayrollSource.Attendance]: 'Attendance',
  [PayrollSource.Safety]: 'Safety',
  [PayrollSource.Other]: 'Other',
  [PayrollSource.Referral]: 'Referral',
  [PayrollSource.SignOn]: 'Sign On',
  [PayrollSource.Retention]: 'Retention',
  [PayrollSource.DropAndHook]: 'D&H',
  [PayrollSource.AvailabilitySolo]: 'Availability Solo',
  [PayrollSource.AvailabilityTeam]: 'Availability Team',
  [PayrollSource.LegWage]: 'Leg Wage',
  [PayrollSource.IncludeSoloMiles]: 'Included Solo Miles',
  [PayrollSource.IncludeTeamMiles]: 'Included Team Miles',
  [PayrollSource.Overtime]: 'Overtime',
  [PayrollSource.Orientation]: 'Orientation',
  [PayrollSource.Training]: 'Training Pay',
  [PayrollSource.DetentionPay]: 'Detention Pay',
  [PayrollSource.HealthCareDeduction]: 'Health Deduction',
  [PayrollSource.HealthCareCredit]: 'Health Credit',
  [PayrollSource.WeeklyAdjustment]: 'Weekly Adjustment',
  [PayrollSource.Services]: 'Services Rendered',
  [PayrollSource.AvailableNoWork]: 'Available No Work',
  [PayrollSource.DayPayCorrection]: 'Day Pay Correction',
  [PayrollSource.MileageSoloRuleCorrection]: 'Solo Mileage Correction (Rule)',
  [PayrollSource.MileageTeamRuleCorrection]: 'Team Mileage Correction (Rule)',
  [PayrollSource.Breakdown]: 'Breakdown',
  [PayrollSource.perDiemSoloMiles]: 'Per Diem (Solo Miles)',
  [PayrollSource.perDiemTeamMiles]: 'Per Diem (Team Miles)',
  [PayrollSource.perDiemSoloMilesPrior]: 'Per Diem (Solo Miles Prior)',
  [PayrollSource.perDiemTeamMilesPrior]: 'Per Diem (Team Miles Prior)',
  [PayrollSource.Weekend]: 'Weekend',
  [PayrollSource.Trainer]: 'Trainer',
  [PayrollSource.Trainee]: 'Trainee',
  [PayrollSource.HolidayPayDay]: 'Holiday (Day)',
  [PayrollSource.HolidayPaySolo]: 'Holiday (Solo)',
  [PayrollSource.HolidayPayTeam]: 'Holiday (Team)',
  [PayrollSource.Fuel]: 'Fuel',
  [PayrollSource.Tolls]: 'Tolls',
  [PayrollSource.Scale]: 'Scale',
  [PayrollSource.DEF]: 'DEF',
  [PayrollSource.Wash]: 'Wash',
  [PayrollSource.Repair]: 'Repair',
  [PayrollSource.AvailabilitySpot]: 'Availability Spot',
  [PayrollSource.LayDown]: 'Lay Down',
  [PayrollSource.BlockFurtherRules]: 'Block Further Rules',
  [PayrollSource.DhCorrection]: 'DH Correction',
  [PayrollSource.DailyOverride]: 'Daily Override',
  [PayrollSource.DayPay6thDayCorrection]: '6th Day Pay Correction',
  [PayrollSource.WageExtraDH]: 'Wage Extra (DH)',
  [PayrollSource.HourlyCorrection]: 'Hourly Correction',
  [PayrollSource.Birthday]: 'Birthday',
  [PayrollSource.MaintCoverage]: 'Maint. Cvrg.',
  [PayrollSource.Efficiency]: 'Efficiency',
  [PayrollSource.DayPayIncremental]: 'Day Pay (Incr.)'
};

export const BonusSourceOnly: PayrollSource[] = [
  PayrollSource.Unknown,
  PayrollSource.Attendance,
  PayrollSource.Safety,
  PayrollSource.Other,
  PayrollSource.Referral,
  PayrollSource.SignOn,
  PayrollSource.Retention,
  PayrollSource.DropAndHook,
  PayrollSource.DOTInspection,
  PayrollSource.Services,
  PayrollSource.Birthday,
  PayrollSource.Efficiency
];

export const ExpenseSourceOnly: PayrollSource[] = [
  PayrollSource.Fuel,
  PayrollSource.Tolls,
  PayrollSource.Scale,
  PayrollSource.DEF,
  PayrollSource.Wash,
  PayrollSource.Repair,
  PayrollSource.HealthCareDeduction,
  PayrollSource.HealthCareCredit,
  PayrollSource.Other
];
export const MiscWorkSourceOnly: PayrollSource[] = [
  PayrollSource.None,
  PayrollSource.Other,
  PayrollSource.AvailableNoWork,
  PayrollSource.CancelledRun,
  PayrollSource.EquipmentMove,
  PayrollSource.DOTInspection,
  PayrollSource.Orientation,
  PayrollSource.Training,
  PayrollSource.DetentionPay,
  PayrollSource.CancelledRun,
  PayrollSource.Referral,
  PayrollSource.Safety,
  PayrollSource.Breakdown
];

export const SpotSourceOnly: PayrollSource[] = [
  PayrollSource.DayPay6th,
  PayrollSource.SafetySpot,
  PayrollSource.Safety,
  PayrollSource.Stop,
  PayrollSource.LiveLoad,
  PayrollSource.PerPiece,
  PayrollSource.Override,
  PayrollSource.Gross
];

export const HolidaySourceOnly: PayrollSource[] = [
  PayrollSource.HolidayPayDay,
  PayrollSource.HolidayPaySolo,
  PayrollSource.HolidayPayTeam,
  PayrollSource.Other
];

export function GetMiscWorkSource(tenantId: string): number[] {
  switch (tenantId.toUpperCase()) {
    case TenantIds.JWI:
      return EnumSelect.ToIdListFilter(PayrollSource, [...MiscWorkSourceOnly, PayrollSource.WeeklyAdjustment]);
    case TenantIds.JSSJ:
      return EnumSelect.ToIdListFilter(PayrollSource, [...MiscWorkSourceOnly, PayrollSource.LayDown]);
    case TenantIds.T27:
      return EnumSelect.ToIdListFilter(PayrollSource, [...MiscWorkSourceOnly, PayrollSource.MaintCoverage]);
    default:
      return EnumSelect.ToIdListFilter(PayrollSource, MiscWorkSourceOnly);
  }
}

export function GetBonusSource(tenantId: string): number[] {
  switch (tenantId.toUpperCase()) {
    case TenantIds.BEI:
      return EnumSelect.ToIdListFilter(PayrollSource, [PayrollSource.Safety, PayrollSource.SafetyTeam]);
    case TenantIds.ESE:
      return EnumSelect.ToIdListFilter(PayrollSource, [...BonusSourceOnly, PayrollSource.HolidayPayDay]);
    case TenantIds.JSSJ:
      return EnumSelect.ToIdListFilter(PayrollSource, [
        ...BonusSourceOnly,
        PayrollSource.DayPay6th,
        PayrollSource.DayPay6thDayCorrection
      ]);
    case TenantIds.GGC:
      return EnumSelect.ToIdListFilter(PayrollSource, [
        ...BonusSourceOnly,
        PayrollSource.DayPay6th,
        PayrollSource.DayPay6thDayCorrection
      ]);
    case TenantIds.JWI:
      return EnumSelect.ToIdListFilter(PayrollSource, [
        ...BonusSourceOnly,
        PayrollSource.DayPay6th,
        PayrollSource.DayPay6thDayCorrection
      ]);
    case TenantIds.ST:
      return EnumSelect.ToIdListFilter(PayrollSource, [
        ...BonusSourceOnly,
        PayrollSource.DayPay6th,
        PayrollSource.DayPay6thDayCorrection
      ]);
    case TenantIds.T27:
      return EnumSelect.ToIdListFilter(PayrollSource, [...BonusSourceOnly, PayrollSource.Incentive]);
    default:
      return EnumSelect.ToIdListFilter(PayrollSource, BonusSourceOnly);
  }
}

export function GetSpotSource(tenantId: string): number[] {
  switch (tenantId.toUpperCase()) {
    default:
      return EnumSelect.ToIdListFilter(PayrollSource, SpotSourceOnly);
  }
}

export function GetHolidaySource(tenantId: string): number[] {
  switch (tenantId.toUpperCase()) {
    default:
      return EnumSelect.ToIdListFilter(PayrollSource, HolidaySourceOnly);
  }
}
